import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import EmployerLayout from '../../layouts/EmployerLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import toast from 'react-hot-toast';

export default function EmployerProfileCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: '', trade_name: '', employer_type: 'company',
    gstin: '', pan: '', registration_number: '',
    contact_person: '', designation: '', website: '',
    address_line: '', pincode: '', description: '',
  });

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.company_name.trim()) return toast.error('Company name is required');
    setLoading(true);
    try {
      await api.post('/employer/profile', form);
      toast.success('Profile created! Pending admin verification.');
      navigate('/employer/dashboard');
    } catch {}
    finally { setLoading(false); }
  }

  return (
    <EmployerLayout>
      <div className="max-w-xl space-y-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Create Employer Profile</h1>
          <p className="text-sm text-gray-500 mt-1">Complete your profile to post requirements and hire workers</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Company Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="font-semibold text-gray-900">Company Information</h2>
            <Input label="Company Name" required value={form.company_name}
              onChange={set('company_name')} placeholder="ABC Constructions Pvt Ltd" />
            <Input label="Trade / Brand Name" value={form.trade_name}
              onChange={set('trade_name')} placeholder="If different from company name" />
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Employer Type</label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
                value={form.employer_type} onChange={set('employer_type')}>
                <option value="company">Company / Private Ltd</option>
                <option value="contractor">Contractor</option>
                <option value="agency">Staffing Agency</option>
                <option value="individual">Individual</option>
                <option value="ngo">NGO / Trust</option>
                <option value="government">Government</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">About Company</label>
              <textarea rows={3} value={form.description} onChange={set('description')}
                placeholder="Brief about your business, work type, and work locations..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none resize-none" />
            </div>
          </div>

          {/* Legal Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="font-semibold text-gray-900">Legal Details <span className="text-gray-400 font-normal text-sm">(optional)</span></h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label="GSTIN" value={form.gstin} onChange={set('gstin')} placeholder="22AAAAA0000A1Z5" />
              <Input label="PAN" value={form.pan} onChange={set('pan')} placeholder="ABCDE1234F" />
            </div>
            <Input label="Registration Number" value={form.registration_number} onChange={set('registration_number')} />
          </div>

          {/* Contact */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="font-semibold text-gray-900">Contact & Address</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label="Contact Person" value={form.contact_person} onChange={set('contact_person')} />
              <Input label="Designation" value={form.designation} onChange={set('designation')} placeholder="HR Manager" />
            </div>
            <Input label="Website" value={form.website} onChange={set('website')} placeholder="https://example.com" />
            <Input label="Office Address" value={form.address_line} onChange={set('address_line')} />
            <Input label="Pincode" value={form.pincode} onChange={set('pincode')} />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
            Your profile will be reviewed by admin before you can post requirements. This typically takes 1–2 business days.
          </div>

          <Button type="submit" fullWidth loading={loading}>Submit for Verification</Button>
        </form>
      </div>
    </EmployerLayout>
  );
}
